import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    createIWSRecord,
    loadIWSRecordsByPlacementId,
    updateIWSRecord
} from '../../../../../store/participantService';
import { loadUserById } from '../../../../../store/userService';
import { getFutureDate, todayDate } from '../../../../../utils/dateFunctions';
import {
    getConfiguredItems,
    getNameFromId,
    getObjFromId
} from '../../../../../utils/directusFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../../utils/userRoles';
import Button from '../../../../formElements/Button';
import SingleSelect from '../../../../formElements/SingleSelect';
import TextAreaField from '../../../../formElements/TextAreaField';
import ActionPlan from '../../../actionPlan/ActionPlan';
import Barriers from '../../../barriers/Barriers';
import { validate } from '../validation/validateIws';

import InWorkSupportTableManagement from './InWorkSupportTableManagement';

import app from '../../../../../app.module.css';
import form from '../../../../../commonStyles/formStyles.module.css';
import local from '../../submissionStyles/iws.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const eventOwnerRoles = [ADVISER, MANAGER, RECRUITMENT_MANAGER];
const _FREQUENCY_NOTE_LENGTH = 750;

const _DAY_OF_WEEK_OPTIONS_ALL = [
    { id: 'monday', name: 'Monday' },
    { id: 'tuesday', name: 'Tuesday' },
    { id: 'wednesday', name: 'Wednesday' },
    { id: 'thursday', name: 'Thursday' },
    { id: 'friday', name: 'Friday' },
    { id: 'saturday', name: 'Saturday' },
    { id: 'sunday', name: 'Sunday' }
];

const normalizeDayOfWeek = (value) => {
    if (typeof value !== 'string') return '';
    const day = value.trim().toLowerCase();
    return _DAY_OF_WEEK_OPTIONS_ALL.some((el) => el.id === day) ? day : '';
};

const InWorkSupport = ({ placementRow }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        frequencyId: '',
        placementId: '',
        iwsEventOwnerId: '',
        dayOfWeek: '',
        frequencyNote: '',
        dueDate: '',
        frequencyWeekNo: '',
        callDate: '',
        workConfidenceId: '',
        callOutcomeId: '',
        note: '',
        notesFromLastCall: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialData, setInitialData] = useState(initialState);
    const [initialIWSOwnerId, setInitialIWSOwnerId] = useState('');
    const [eventOwners, setEventOwners] = useState([]);
    const [isExistingEventOwnerExpired, setIsExistingEventOwnerExpired] = useState(false);
    const [iwsRecords, setIwsRecords] = useState([]);
    const [iwsRecordsForPlacement, setIwsRecordsForPlacement] = useState([]);
    const [arrayFrequencyDetails, setArrayFrequencyDetails] = useState([]);
    const [dayOfWeekOptions, setDayOfWeekOptions] = useState([]);
    const [callAddedTodayAlready, setCallAddedTodayAlready] = useState(false);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const users = useSelector((state) => state.entities.userService.users);
    const usersById = useSelector((state) => state.entities.userService.usersById);
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const iwsRecordsByPlacementId = useSelector(
        (state) => state.entities.participantService.iwsRecordsByPlacementId
    );
    const frequencyDetails = useSelector(
        (state) => state.entities.directusService.frequencyDetails
    );
    const callOutcomes = useSelector((state) => state.entities.directusService.callOutcomes);
    const workConfidence = useSelector((state) => state.entities.directusService.workConfidence);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (
            Object.keys(iwsRecordsByPlacementId)?.length > 0 &&
            placementRow.id in iwsRecordsByPlacementId
        ) {
            setIwsRecords(iwsRecordsByPlacementId[placementRow.id].iwsRecords);
        } else dispatch(loadIWSRecordsByPlacementId(placementRow.id));
    }, [iwsRecordsByPlacementId]);

    useEffect(() => {
        if (roles.some((el) => eventOwnerRoles.includes(el)))
            onUpdate('iwsEventOwnerId', loggedInUser.id);
    }, [loggedInUser]);

    useEffect(() => {
        const newEventOwners = users
            .filter((el) => el.userTypes?.find((entry) => eventOwnerRoles.includes(entry.role)))
            .map((el) => ({ id: el.id, name: el.loginEmailAddress }))
            .sort((a, b) => a.name.localeCompare(b.name));
        setEventOwners(newEventOwners);
    }, [users]);

    useEffect(() => {
        if (frequencyDetails?.length < 1 || !currentParticipant?.contractId) return;
        setArrayFrequencyDetails(
            getConfiguredItems(frequencyDetails, currentParticipant?.contractId)
        );
    }, [frequencyDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (iwsRecords?.length < 1) return;
        const updatedIwsRecords = iwsRecords.map((el) => ({
            ...el,
            workConfidenceName: getNameFromId(workConfidence, el.workConfidenceId),
            outcomeName: getNameFromId(callOutcomes, el.callOutcomeId),
            contactedBy:
                users.find((entry) => entry.id === el.contactedById)?.loginEmailAddress || ''
        }));
        setIwsRecordsForPlacement(updatedIwsRecords);

        const callToday = updatedIwsRecords.find(
            (el) => todayDate() === el.dueDate.substring(0, 10)
        );
        let initialOwnerId = '';
        if (callToday) {
            const call = { ...callToday, dayOfWeek: normalizeDayOfWeek(callToday.dayOfWeek) };
            setNewEntry(call);
            setInitialData(call);
            initialOwnerId = call.iwsEventOwnerId || '';
            const notCalled =
                !callToday.callOutcomeId ||
                getNameFromId(callOutcomes, callToday.callOutcomeId).toLowerCase() === 'due' ||
                getNameFromId(callOutcomes, callToday.callOutcomeId).toLowerCase() === 'missed';
            setCallAddedTodayAlready(!notCalled);
        } else {
            const firstFutureDatedCall = updatedIwsRecords.find((el) =>
                getFutureDate(el.dueDate.substring(0, 10))
            );
            if (firstFutureDatedCall) {
                setNewEntry({
                    ...firstFutureDatedCall,
                    dayOfWeek: normalizeDayOfWeek(firstFutureDatedCall.dayOfWeek)
                });
                initialOwnerId = firstFutureDatedCall.iwsEventOwnerId || '';
            }
        }
        if (initialOwnerId) {
            setInitialIWSOwnerId(initialOwnerId);
            const owner = users.find((el) => el.id === initialOwnerId);
            if (!owner && !(initialOwnerId in usersById)) dispatch(loadUserById(initialOwnerId));
        } else if (initialIWSOwnerId) setInitialIWSOwnerId('');
    }, [iwsRecords]);

    useEffect(() => {
        if (!initialIWSOwnerId || isExistingEventOwnerExpired || !(initialIWSOwnerId in usersById))
            return;
        const ownerServiceIds = [
            ...usersById[initialIWSOwnerId].otherServices,
            usersById[initialIWSOwnerId].primaryService
        ].filter((el) => el);
        const isExAdviser = !ownerServiceIds.includes(currentParticipant.serviceId);
        setIsExistingEventOwnerExpired(isExAdviser);
        if (isExAdviser) {
            const tempOwner = {
                id: usersById[initialIWSOwnerId].id,
                name: usersById[initialIWSOwnerId].loginEmailAddress
            };
            setEventOwners((prev) => [...prev, tempOwner]);
        }
    }, [usersById, initialIWSOwnerId]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (
            [
                `IWS Record for ${placementRow.businessName} has been added`,
                `IWS Record for ${placementRow.businessName} has been updated`
            ].includes(successMessage)
        ) {
            dispatch(loadIWSRecordsByPlacementId(placementRow.id));
            setIsUpdateButtonDisabled(false);
        }
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateEventOwner = (chosenId) => {
        if (isExistingEventOwnerExpired) {
            const existingOwnerId = eventOwners.find((el) => el.id === initialIWSOwnerId)?.id || '';
            if (existingOwnerId) {
                setEventOwners((prev) => prev.filter((el) => el.id !== existingOwnerId));
                setIsExistingEventOwnerExpired(false);
            }
        }
        onUpdate('iwsEventOwnerId', chosenId);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialData);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            dayOfWeek: normalizeDayOfWeek(newEntry.dayOfWeek),
            participantId: currentParticipant.id,
            placementId: placementRow.id
        };
        !newEntry.id
            ? dispatch(createIWSRecord(payload, placementRow.businessName))
            : dispatch(updateIWSRecord(payload, placementRow.businessName));
    };

    useEffect(() => {
        const weekendsWorkingDays = !!contractConstants?.weekendAsWorkingDay;
        setDayOfWeekOptions(
            weekendsWorkingDays
                ? _DAY_OF_WEEK_OPTIONS_ALL
                : _DAY_OF_WEEK_OPTIONS_ALL.filter(
                      (el) => el.id !== 'saturday' && el.id !== 'sunday'
                  )
        );

        // If contract config changes to disallow weekends, clear invalid existing value.
        if (!weekendsWorkingDays && ['saturday', 'sunday'].includes(newEntry.dayOfWeek)) {
            onUpdate('dayOfWeek', '');
        }
    }, [contractConstants?.weekendAsWorkingDay]);

    return (
        <div className={local.contentWrapper}>
            <form className={local.formWrapper} onSubmit={onSubmit}>
                <h2 className={app.sectionHeading}>IWS Overview:</h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="frequencyId"
                            label="Frequency"
                            placeholder="Select frequency..."
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            menuItems={arrayFrequencyDetails}
                            selectedId={newEntry.frequencyId || ''}
                            selected={getObjFromId(frequencyDetails, newEntry.frequencyId)}
                            error={errors.frequencyId}
                            onChange={(chosenId) => onUpdate('frequencyId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="iwsEventOwnerId"
                            label="IWS Event Owner"
                            placeholder="Select IWS event owner..."
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={true}
                            capitalizeList={false}
                            menuItems={eventOwners}
                            selectedId={newEntry?.iwsEventOwnerId || ''}
                            selected={getObjFromId(eventOwners, newEntry.iwsEventOwnerId)}
                            error={errors.iwsEventOwnerId}
                            onChange={(chosenId) => onUpdateEventOwner(chosenId)}
                        />
                    </div>
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="dayOfWeek"
                            label="Day of the Week"
                            placeholder="Select day of the week..."
                            disabled={!hasRole(acceptedRoles, roles)}
                            mandatory={false}
                            onLoadMoreItems={() => {}}
                            menuItems={dayOfWeekOptions}
                            selectedId={newEntry?.dayOfWeek || ''}
                            selected={getObjFromId(_DAY_OF_WEEK_OPTIONS_ALL, newEntry.dayOfWeek)}
                            onChange={(chosenId) => onUpdate('dayOfWeek', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn} />
                </div>

                <TextAreaField
                    id="frequencyNote"
                    label="Frequency Notes"
                    placeholder="Enter frequency notes"
                    disabled={!hasRole(acceptedRoles, roles)}
                    maxLength={_FREQUENCY_NOTE_LENGTH}
                    value={newEntry?.frequencyNote || ''}
                    count={`${newEntry.frequencyNote?.length || 0}/${_FREQUENCY_NOTE_LENGTH}`}
                    onChange={(e) => onUpdate('frequencyNote', e.target.value)}
                />

                <Button
                    id="iwsOverViewFormUpdateButton"
                    disabled={
                        !hasRole(acceptedRoles, roles) ||
                        (Object.keys(iwsRecordsForPlacement).length > 0 &&
                            !callAddedTodayAlready) ||
                        isUpdateButtonDisabled
                    }
                    content="Update"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>

            <div className={app.divider} />

            <h2 className={app.sectionHeading}>IWS Schedule:</h2>
            <InWorkSupportTableManagement
                acceptedRoles={acceptedRoles}
                currentCall={newEntry}
                rows={iwsRecordsForPlacement}
                placement={placementRow}
            />

            {contractConstants?.displayBarrierTableInIWS ? (
                <div className={local.contentWrapper}>
                    <Barriers />{' '}
                </div>
            ) : null}

            {contractConstants?.displayActionTableInIWS ? (
                <div className={local.contentWrapper}>
                    <ActionPlan />{' '}
                </div>
            ) : null}
        </div>
    );
};

InWorkSupport.propTypes = {
    placementRow: PropTypes.object
};

export default InWorkSupport;
